import React, { useState, useEffect } from "react";
import { Select } from "antd";
import { useDataContext } from "../../context/DataContext";
import Count from "./Count";
import QMax from "./Qmax";
import Time from "./Time";
import Speed from "./Speed";
import SectionMap from "./SectionMap";

const { Option } = Select;

const SectionAnalysis = ({ data, selectedIdScenario }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [dataJson, setDataJson] = useState([]);

  // Use DataContext for client-specific data (default or lavagna)
  const { graficiDataJson, losDataJson, dataLoaded } = useDataContext();

  useEffect(() => {
    if (dataLoaded && graficiDataJson && losDataJson && selectedIdScenario) {
      // Find scenarios in both data sources using client-specific data
      const graficiScenario = graficiDataJson.scenario?.find(
        (scenario) => scenario.replicaAvg === selectedIdScenario
      );
      const losScenario = losDataJson.scenario?.find(
        (scenario) => scenario.replicaAvg === selectedIdScenario
      );

      // Helper to normalize IDs (handles "455.0" vs 455 format)
      const normalizeId = (id) => {
        if (id === null || id === undefined) return null;
        const parsed = typeof id === 'string' ? parseFloat(id) : id;
        return Number.isInteger(parsed) ? parsed : Math.floor(parsed);
      };

      if (graficiScenario || losScenario) {
        // Create a map of all streets from grafici (has detailed time-series data)
        const streetsMap = new Map();

        if (graficiScenario && graficiScenario.sezioni) {
          graficiScenario.sezioni.forEach(section => {
            const normalizedId = normalizeId(section.id);
            streetsMap.set(normalizedId, { ...section, id: normalizedId });
          });
        }

        // Add streets from LOS that might not be in grafici (they won't have charts but should appear)
        if (losScenario && losScenario.LosNodi) {
          losScenario.LosNodi.forEach(node => {
            if (node.sezioni) {
              Object.values(node.sezioni).forEach(section => {
                const normalizedId = normalizeId(section.idSec);
                if (!streetsMap.has(normalizedId)) {
                  // Street exists in LOS but not in grafici - add basic info
                  streetsMap.set(normalizedId, {
                    id: normalizedId,
                    nome: section.nomeSec,
                    // No time-series data, but include LOS info
                    losInfo: {
                      delaySec: section.delaySec,
                      losSec: section.losSec,
                      color: section.color,
                      count: section.count
                    }
                  });
                }
              });
            }
          });
        }

        // Convert to array and sort by id
        const streetsArray = Array.from(streetsMap.values()).sort((a, b) => a.id - b.id);
        setDataJson(streetsArray);
      } else {
        setDataJson([]);
      }
    }
  }, [graficiDataJson, losDataJson, selectedIdScenario, dataLoaded]);

  const handleStreetChange = (value) => {
    if (value) {
      setSelectedId(value);
    } else {
      setSelectedId(null);
    }
  };

  return (
    <div>
      {/* Street Selector */}
      <div className="street-selector" style={{ marginBottom: 20 }}>
        <Select
          showSearch
          style={{ width: 300 }}
          placeholder="Seleziona la via"
          value={selectedId || undefined}
          onChange={handleStreetChange}
          optionFilterProp="children"
        >
          {dataJson.map((street) => (
            <Option key={street.id} value={street.id}>
              {street.nome || `No name - ${street.id}`}
            </Option>
          ))}
        </Select>
      </div>

      {/* Map Section - Shows the same map as SimpleMap but with road highlighting */}
      <SectionMap
        selectedIdScenario={selectedIdScenario}
        selectedSectionId={selectedId}
        selectedSectionName={dataJson.find(s => s.id === selectedId)?.nome}
      />

      {/* Charts Section */}
      {selectedId && dataJson.length > 0 && (() => {
        const selectedStreet = dataJson.find(s => s.id === selectedId);
        const hasChartData = selectedStreet && selectedStreet.serieTemporale;

        if (hasChartData) {
          return (
            <div className="charts" style={{ display: "grid", gap: 20, marginTop: 20 }}>
              <Count streetName={selectedId} data={dataJson} />
              <QMax streetName={selectedId} data={dataJson} />
              <Time streetName={selectedId} data={dataJson} />
              <Speed streetName={selectedId} data={dataJson} />
            </div>
          );
        } else if (selectedStreet && selectedStreet.losInfo) {
          return (
            <div style={{ marginTop: 20, padding: 20, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
              <h3>Informazioni LOS disponibili per questa strada:</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginTop: 16 }}>
                <div>
                  <strong>Nome:</strong> {selectedStreet.nome}
                </div>
                <div>
                  <strong>LOS:</strong> <span style={{
                    backgroundColor: selectedStreet.losInfo.color,
                    padding: '2px 8px',
                    borderRadius: 4,
                    color: ['#F6ED60', '#FDB64E'].includes(selectedStreet.losInfo.color) ? '#333' : '#fff'
                  }}>{selectedStreet.losInfo.losSec}</span>
                </div>
                <div>
                  <strong>Ritardo:</strong> {selectedStreet.losInfo.delaySec} sec
                </div>
                <div>
                  <strong>Veicoli:</strong> {selectedStreet.losInfo.count}
                </div>
              </div>
              <p style={{ marginTop: 16, fontStyle: 'italic', color: '#666' }}>
                I grafici dettagliati non sono disponibili per questa sezione.
              </p>
            </div>
          );
        } else {
          return (
            <div style={{ marginTop: 20, padding: 20, backgroundColor: '#fff3cd', borderRadius: 8 }}>
              <p>Nessun dato disponibile per questa strada.</p>
            </div>
          );
        }
      })()}
    </div>
  );
};

export default SectionAnalysis;
